import * as React from 'react'
import {useState, useMemo} from 'react'
import {DataGridPro, GridToolbar} from '@mui/x-data-grid-pro'
import axios from 'axios'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import TextField from '@mui/material/TextField'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import InfoIcon from '@mui/icons-material/Info'
import './BILAwards.css'
import {
  Assignment,
  Functions,
  MergeType,
  Person,
  Business,
  Public as Government,
  Flight,
  NaturePeople,
} from '@mui/icons-material'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'

function escapeRegExp(value: string): string {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&')
}

interface QuickSearchToolbarProps {
  clearSearch: () => void
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  value: string
}
function lightenColor(hex: string, percent: number) {
  const ratio = Math.min(Math.max(percent, 0), 1)
  const r = parseInt(hex.substring(1, 3), 16)
  const g = parseInt(hex.substring(3, 5), 16)
  const b = parseInt(hex.substring(5, 7), 16)
  const newR = Math.round(r + (255 - r) * ratio).toString(16)
  const newG = Math.round(g + (255 - g) * ratio).toString(16)
  const newB = Math.round(b + (255 - b) * ratio).toString(16)
  return `#${newR}${newG}${newB}`
}

function QuickSearchToolbar(props: QuickSearchToolbarProps) {
  return (
    <Box
      sx={{
        p: 0.5,
        pb: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <TextField
        variant='standard'
        value={props.value}
        onChange={props.onChange}
        placeholder='Search…'
        InputProps={{
          startAdornment: <SearchIcon fontSize='small' />,
          endAdornment: (
            <IconButton
              title='Clear'
              aria-label='Clear'
              size='small'
              style={{visibility: props.value ? 'visible' : 'hidden'}}
              onClick={props.clearSearch}
            >
              <ClearIcon fontSize='small' />
            </IconButton>
          ),
        }}
      />
      <GridToolbar />
    </Box>
  )
}

export default function BILAwards() {
  const [data, setData] = React.useState<any[]>([])
  const [searchText, setSearchText] = React.useState('')
  const [rows, setRows] = React.useState<any[]>([])
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 25,
    page: 0,
  })

  const [open, setOpen] = useState(false)
  const [openInstructions, setOpenInstructions] = useState(false) // New state variable for the Instructions dialog
  const [showRecipient, setShowRecipient] = useState(true)
  const [showRecipientType, setShowRecipientType] = useState(true)
  const [showPeriodOfAvailability, setShowPeriodOfAvailability] = useState(true)
  const [showSubcategory, setShowSubcategory] = useState(true)
  const [fundingType, setFundingType] = useState('all') // Possible values: 'IRA', 'IIJA', 'all'
  const [awardStatus, setAwardStatus] = useState('all') // Possible values: 'Awarded', 'Announced', 'all'

  // ... (existing handler functions)

  const handleClickOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  // New handler functions for the Instructions dialog
  const handleClickOpenInstructions = () => {
    setOpenInstructions(true)
  }

  const handleCloseInstructions = () => {
    setOpenInstructions(false)
  }

  const handleFundingTypeChange = (type) => {
    setFundingType(type)

    // Toggle visibility of columns based on funding type
    const isIRA = type === 'IRA'
    setShowRecipient(!isIRA)
    setShowRecipientType(!isIRA)
    setShowPeriodOfAvailability(!isIRA)
    setShowSubcategory(!isIRA)
  }

  const handleAwardStatusChange = (status) => {
    setAwardStatus(status)
  }

  // Update useEffect for fetching data and applying filters
  React.useEffect(() => {
    axios
      .get(process.env.REACT_APP_API_ENDPOINT + '/bli')
      .then((response) => {
        let filteredData = response.data

        if (fundingType !== 'all') {
          filteredData = filteredData.filter((item) => item.funding_source === fundingType)
        }

        if (awardStatus !== 'all') {
          filteredData = filteredData.filter((item) => item.status === awardStatus)
        }

        setData(filteredData)
      })
      .catch((error) => {
        console.error('There was an error!', error)
      })
  }, [fundingType, awardStatus])

  React.useEffect(() => {
    const updatedRows = data.map((row, index) => ({
      ...row,
      id: index + 1,
    }))
    setRows(updatedRows)
  }, [data])

  const categoryColors: Record<string, string> = {
    Resilience: lightenColor('#c3002f', 0.8),
    'Public Transportation': lightenColor('#001970', 0.8),
    'Electric Vehicles, Buses and Ferries': lightenColor('#6d3a5d', 0.8),
    'Environmental Remediation': lightenColor('#245d38', 0.8),
    Broadband: lightenColor('#ffd100', 0.8),
    Safety: lightenColor('#7a853b', 0.8),
    'Airports and Federal Aviation Administration Facilities': lightenColor('#35647e', 0.8),
    Other: '#C0C0C0',
    Water: lightenColor('#6ec4e8', 0.8),
    'Roads, Bridges and Major Projects': lightenColor('#001970', 0.8),
    'Clean Energy and Power': lightenColor('#6d3a5d', 0.8),
    'Ports and Waterways': lightenColor('#245d38', 0.8),
  }

  const requestSearch = (searchValue: string) => {
    setSearchText(searchValue)
    const searchRegex = new RegExp(escapeRegExp(searchValue), 'i')
    const filteredRows = data
      .map((row, index) => ({
        ...row,
        id: index + 1,
      }))
      .filter((row: any) => {
        return Object.keys(row).some((field: any) => {
          if (row[field] !== null && row[field] !== undefined) {
            return searchRegex.test(row[field].toString())
          } else {
            return false
          }
        })
      })
    setRows(filteredRows)
  }

  type ColumnNames = {
    program_name: string
    funding: string
    subcategory: string
    state_name: string
    county: string
    project_name: string
    type: string
    'Period of Availability': string
    recipient: string
    recipient_type: string
    program_description: string
  }

  const columnNames: ColumnNames = {
    program_name: 'Program Name',
    funding: 'Funding',
    subcategory: 'Subcategory',
    state_name: 'State Name',
    county: 'County',
    project_name: 'Project Name',
    type: 'Type',
    'Period of Availability': 'Period of Availability',
    recipient: 'Recipient',
    recipient_type: 'Recipient Type',
    program_description: 'Program Description',
  }

  const columnOrder: (keyof ColumnNames)[] = [
    'project_name',
    'program_name',
    'funding',
    'type',
    'recipient',
    'recipient_type',
    'Period of Availability',
    'county',
    'subcategory',
  ]

  // Create an array of columns with renamed headers, sorted according to the column order
  const columns = columnOrder.map((key, index) => {
    let column: any = {
      field: key,
      headerName: columnNames[key] || key,
      flex: 1,
      headerClassName: 'super-app-theme--header',
    }

    // If the current column is the first or second, make it wider
    if (index === 0 || index === 1) {
      column = {...column, flex: 2}
    }
    if (index === 7) {
      column = {...column, flex: 1.5}
    }

    // Conditionally include/exclude columns
    if (key === 'recipient' && !showRecipient) return null
    if (key === 'recipient_type' && !showRecipientType) return null
    if (key === 'Period of Availability' && !showPeriodOfAvailability) return null
    if (key === 'subcategory' && !showSubcategory) return null

    if (key === 'funding') {
      column = {
        ...column,
        valueFormatter: ({value}: {value: any}) => {
          if (value === '' || value === null) {
            return 'TBD'
          } else if (typeof value === 'string' || typeof value === 'number') {
            const numberValue = typeof value === 'string' ? parseFloat(value) : value
            if (!isNaN(numberValue)) {
              return `$${numberValue.toLocaleString('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}`
            }
          }
          return ''
        },
        sortComparator: (v1: any, v2: any, cellParams1: any, cellParams2: any) => {
          const val1 =
            v1 && (typeof v1 === 'string' || typeof v1 === 'number')
              ? parseFloat(v1.toString().replace(/[$,]/g, '')) || 0
              : 0
          const val2 =
            v2 && (typeof v2 === 'string' || typeof v2 === 'number')
              ? parseFloat(v2.toString().replace(/[$,]/g, '')) || 0
              : 0
          return val1 - val2
        },
        renderCell: (params) => (
          <div style={{fontWeight: 'bold', fontSize: 'larger'}}>{params.formattedValue}</div>
        ),
      }
    }

    if (key === 'subcategory') {
      column = {
        ...column,
        renderCell: (params) => {
          const category = params.row.subcategory
          const bgColor = categoryColors[category] || 'transparent' // default to transparent if category not found
          return (
            <>
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: bgColor,
                  zIndex: -1,
                }}
              ></div>
              <div
                style={{display: 'flex', alignItems: 'flex-start', backgroundColor: 'transparent'}}
              >
                {params.value}
              </div>
            </>
          )
        },
      }
    }

    // Inside your columns mapping
    if (key === 'recipient_type') {
      column = {
        ...column,
        renderCell: (params) => {
          let IconComponent: React.ElementType | null = null
          let label = params.value || '' // Using the value directly if it's not one of the expected values
          let color = 'gray' // Default color

          switch (params.value) {
            case 'Direct':
              IconComponent = Person
              color = '#c3002f' // Red - PMS 200C
              break
            case 'Local':
              IconComponent = Business
              color = '#65503c' // Yellow - PMS 109C
              break
            case 'Local - Airport':
              IconComponent = Flight
              color = '#001970' // Blue - PMS 2748C
              break
            case 'State':
              IconComponent = Government
              color = '#6d3a5d' // Purple - PMS 5125C
              break
            case 'Tribe':
              IconComponent = NaturePeople
              color = '#245d38' // Tree Green - PMS 7483C
              break
            default:
              label = params.value || 'Unknown' // Handle unexpected values
              break
          }

          return (
            <Chip
              icon={IconComponent ? <IconComponent style={{color}} /> : undefined}
              label={label}
              style={{backgroundColor: 'transparent', border: '1px solid', color: color}}
              size='small'
            />
          )
        },
      }
    }

    if (key === 'type') {
      column = {
        ...column,
        renderCell: (params) => {
          let IconComponent: React.ElementType | null = null
          let label = ''
          let color = ''

          if (params.value === 'Discretionary') {
            IconComponent = Assignment
            label = 'Competitive'
            color = '#6d3a5d'
          } else if (params.value === 'Formula') {
            IconComponent = Functions
            label = 'Formula'
            color = '#245d38'
          } else if (params.value === 'Both') {
            IconComponent = MergeType
            label = 'Both'
            color = '#001970'
          }

          return (
            <Chip
              icon={IconComponent ? <IconComponent style={{color}} /> : undefined}
              label={label}
              style={{backgroundColor: 'transparent', border: '1px solid', color: color}}
              size='small'
            />
          )
        },
      }
    }

    return column
  })
  const headerHeight = 100 // Adjust this value based on your actual header height
  const filteredColumns = columns.filter((column) => column != null)
  // Calculate the available height for the DataGrid
  const dataGridHeight = `calc(100vh - ${headerHeight}px)`

  return (
    <div className='card'>
      {/* 1. Change the title */}
      <Typography variant='h4' align='center' gutterBottom>
        Infrastructure Investment and Jobs Act (IIJA) and Inflation Reduction Act (IRA) Colorado
        Funding Table
      </Typography>

      {/* 2. Update the note */}
      <Typography variant='body1' align='center' gutterBottom>
        *IIJA is also known as the Bipartisan Infrastructure Law (BIL)
      </Typography>
      <br />

      {/* Funding Source Section */}
      <div className='mb-3 text-center'>
        <Typography variant='h5'>Select Funding Source</Typography>
        <div className='d-flex justify-content-center mt-2'>
          {/* Funding Source Buttons */}
          <Button
            variant={fundingType === 'IRA' ? 'contained' : 'outlined'}
            color='primary'
            onClick={() => handleFundingTypeChange('IRA')}
            size='medium'
            className='mr-2'
          >
            IRA Funds
          </Button>
          <Button
            variant={fundingType === 'BIL' ? 'contained' : 'outlined'}
            color='primary'
            onClick={() => handleFundingTypeChange('BIL')}
            size='medium'
            className='mr-2'
          >
            IIJA (BIL) Funds
          </Button>
          <Button
            variant={fundingType === 'all' ? 'contained' : 'outlined'}
            color='primary'
            onClick={() => handleFundingTypeChange('all')}
            size='medium'
          >
            Show All
          </Button>
        </div>
      </div>

      <br />

      {/* Funding Status Section */}
      <div className='mb-3 text-center'>
        <Typography variant='h5'>Select Funding Status</Typography>
        <div className='d-flex justify-content-center mt-2'>
          {/* Funding Status Buttons */}
          <Button
            variant={awardStatus === 'USASpending' ? 'contained' : 'outlined'}
            color='success'
            onClick={() => handleAwardStatusChange('USASpending')}
            size='medium'
            className='mr-2'
          >
            Show Awarded
          </Button>
          <Button
            variant={awardStatus === 'Agency Data Call' ? 'contained' : 'outlined'}
            color='primary'
            onClick={() => handleAwardStatusChange('Agency Data Call')}
            size='medium'
            className='mr-2'
          >
            Show Announced
          </Button>
          <Button
            variant={awardStatus === 'all' ? 'contained' : 'outlined'}
            color='warning'
            onClick={() => handleAwardStatusChange('all')}
            size='medium'
          >
            Show All
          </Button>
        </div>
      </div>
      <br />
      {/* Funding Status Section */}

      <br />
      <DataGridPro
        rows={rows}
        // getRowHeight={() => 'auto'}
        columns={filteredColumns}
        components={{Toolbar: QuickSearchToolbar}}
        componentsProps={{
          toolbar: {
            value: searchText,
            onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
              requestSearch(event.target.value),
            clearSearch: () => requestSearch(''),
          },
        }}
        getCellClassName={(params) => {
          if (params.field === 'subcategory') {
            const category = params.value
            const categoryClassMap = {
              Resilience: 'cell-bg-resilience',
              'Public Transportation': 'cell-bg-public-transportation',
              'Electric Vehicles, Buses and Ferries': 'cell-bg-electric-vehicles',
              'Environmental Remediation': 'cell-bg-environmental-remediation',
              Broadband: 'cell-bg-broadband',
              Safety: 'cell-bg-safety',
              'Airports and Federal Aviation Administration Facilities': 'cell-bg-airports',
              Other: 'cell-bg-other',
              Water: 'cell-bg-water',
              'Roads, Bridges and Major Projects': 'cell-bg-roads',
              'Clean Energy and Power': 'cell-bg-clean-energy',
              'Ports and Waterways': 'cell-bg-ports',
            }
            return categoryClassMap[category] || ''
          }
          return ''
        }}
        className='datagrid'
        getRowClassName={(params) => (Number(params.id) % 2 === 0 ? 'even-row' : '')}
        // Enable pagination
        pagination
        // Set the page size
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        sx={{
          '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {py: '8px'},
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {py: '15px'},
          '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {py: '22px'},
        }}
        // sortModel={[
        //   {
        //     field: 'funding',
        //     sort: 'desc', // 'asc' for ascending and 'desc' for descending
        //   },
        // ]}
        getDetailPanelContent={({row}) => (
          <div style={{padding: '10px'}}>
            <Typography variant='h5' gutterBottom>
              {row.project_name}
            </Typography>
            <Typography variant='body1'>
              <span role='img' aria-label='description' style={{fontSize: '1.5em'}}>
                📝
              </span>{' '}
              <span style={{fontWeight: 'bold'}}>Description:</span> {row.program_description}
            </Typography>
            <Typography variant='body1'>
              <span role='img' aria-label='funding' style={{fontSize: '1.5em'}}>
                💰
              </span>{' '}
              <span style={{fontWeight: 'bold'}}>Funding:</span> $
              {row.funding ? row.funding.toLocaleString() : 'N/A'}
            </Typography>
            <Typography variant='body1'>
              <span role='img' aria-label='status' style={{fontSize: '1.5em'}}>
                🚀
              </span>{' '}
              <span style={{fontWeight: 'bold'}}>Status:</span> {row.status}
            </Typography>

            {row.more_details_link && (
              <Typography variant='body1'>
                <span role='img' aria-label='more-details' style={{fontSize: '1.5em'}}>
                  🔗
                </span>
                <a href={row.more_details_link} target='_blank' rel='noopener noreferrer'>
                  More Details
                </a>
              </Typography>
            )}
          </div>
        )}
        getDetailPanelHeight={({row}) => 200}
        style={{height: dataGridHeight}} // Set the height to occupy the remaining space
      />

      <br />
      <br />
      <div style={{textAlign: 'center'}}>
        <Button
          variant='outlined'
          color='primary'
          onClick={handleClickOpen}
          style={{fontSize: 'larger'}} // Increase the font size
        >
          Data Source Information
        </Button>
        <IconButton color='primary' onClick={handleClickOpen}>
          <InfoIcon />
        </IconButton>

        {/* Add another button for Page Instructions */}
        <Button
          variant='outlined'
          color='primary'
          onClick={handleClickOpenInstructions}
          style={{fontSize: 'larger', marginLeft: '10px'}} // Increase the font size
        >
          Page Instructions
        </Button>
      </div>

      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Data Notes and Sources</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {' '}
            <Typography variant='body1' style={{padding: '0 20px', textAlign: 'left'}}>
              <strong>Announced vs. Awarded Data Notes and Sources</strong>
              <br />
              <br />
              <strong>Announcement Funding Data Source:</strong> Each month, Agencies submit reports
              on their funding announcements made within the previous month, often through Agency
              press releases or apportionment tables. The announcement data on the Luminance website
              is synchronized with updates from the GSA website, typically on a monthly basis,
              although occasional delays may occur.
              <br />
              <br />
              <strong>Awarded Funding Data Source:</strong> Awarded data is retrieved from
              USASpending.gov, which encompasses all awards exceeding $100,000 (as of the report's
              data date) labeled with a Disaster Emergency Fund Code (DEFC) of “1” or “Z,” denoting
              their connection to the Bipartisan Infrastructure Law (IIJA).
              <br />
              <br />
              <strong>Notes: </strong>
              Announced funding, which is captured from Agency press releases, is preliminary and
              non-binding and therefore may be contingent on grantees meeting certain requirements,
              whereas awarded funding has been downloaded from USASpending.gov and represents actual
              obligations, which are defined as a legally binding agreement that will result in
              outlays, either immediately or in the future. Because they represent different
              statuses, announced and awarded funding should not be summed or compared.
              <br />
              <br />
              For comprehensive insights into this data site, please refer to our technical report
              available{' '}
              <a href='http://bit.ly/IIJA_CO' target='_blank' rel='noopener noreferrer'>
                here
              </a>
              .
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {/* Page Instructions Dialog */}
      <Dialog open={openInstructions} onClose={handleCloseInstructions}>
        <DialogTitle style={{backgroundColor: '#16384a', color: 'white'}}>
          Page Instructions
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography variant='body1' paragraph marginTop={1}>
              <strong>Changing Page View:</strong>
              Change page view by clicking on the <strong>“DENSITY”</strong> button.
            </Typography>

            <Typography variant='body1' paragraph>
              <strong>Column and Filters:</strong>
              Filter out columns and search for certain conditions by using the
              <strong>“COLUMNS”</strong> and <strong>“FILTERS”</strong> buttons.
            </Typography>

            <Typography variant='body1' paragraph>
              <strong>Sorting:</strong>
              Sort by any of the columns by clicking the header title at the top.
            </Typography>

            <Typography variant='body1' paragraph>
              <strong>Export Data:</strong>
              Data is exportable into a CSV directly from this page based on what is showing with
              current filters. Use the <strong>“EXPORT”</strong> button.
            </Typography>

            <Typography variant='body1' paragraph>
              <strong>Funding Types:</strong>
              Type of funding includes 1) formula, which means anyone can get the funding if they
              meet the criteria of the funding formula and 2) competitive, which means Colorado is
              competing against other states for the funds.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInstructions} color='primary'>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant='body2' align='center' gutterBottom style={{marginTop: '1em'}}>
        Invest.gov (2024). Investing in America Map. Retrieved from
        https://www.whitehouse.gov/wp-content/uploads/2023/11/Invest.gov_PublicInvestments_Map_Data_CURRENT.xlsx.
      </Typography>
    </div>
  )
}
